import type { NavigationGuard } from 'vue-router'
import { useTokenStore } from '@/stores/token'
import { EPage } from '@/shared/enum/page.enum'
import { useToken } from '@/shared/composables/useToken'
import { useTokenSwitcher } from '@/shared/composables/useTokenSwitcher'

const PROTECTED_ROUTES = [EPage.PACKAGES]

/**
 * Navigation guard that checks if there is an authentication token in the URL.
 * If a token exists, it saves it to local storage and redirects to the appropriate page.
 * If no token exists, it allows normal navigation.
 * @param {Object} to - Object representing the route being navigated to.
 * @param {Object} _from - Object representing the route being navigated from.
 * @param {Function} next - Function to continue with the navigation.
 * @returns {Promise} A promise that resolves when the token verification is complete.
 */
const urlTokenGuard: NavigationGuard = async (to, _from, next) => {
  const { setMainToken } = useTokenSwitcher()
  const { t, ...query } = to.query
  const token = t as string

  if (!token) {
    next()
    return
  }

  const tokenStore = useTokenStore()
  setMainToken(token)
  tokenStore.setToken(token)

  const { isProfiledToken, msisdn } = useToken()

  if (!to.name || to.name === EPage.LANDING) {
    next({ name: EPage.LANDING, query: { ...query } })
    return
  }

  if (!PROTECTED_ROUTES.includes(to.name as EPage)) {
    next({ ...to, query: { ...query } })
    return
  }

  if (!msisdn.value) {
    next({ name: EPage.LANDING, query: { ...query } })
    return
  }

  try {
    if (isProfiledToken.value) {
      next({ ...to, query: { ...query } })
    } else {
      next({ name: EPage.LANDING, query: { ...query } })
    }
  } catch (error) {
    next({ name: EPage.GENERIC_ERROR })
  }
}

export default urlTokenGuard
